import { api } from '../client';
import type { Criticality } from '@/lib/criticality';

export type StockStatus = 'Optimal' | 'Low' | 'Stockout' | 'Overstock';

export interface InventoryItem {
  id: string;
  sku: string;
  name: string;
  description?: string;
  category: string;
  subcategory?: string;
  unit: string;
  companyId: string;
  company?: { id: string; name: string; displayName: string };
  minStockLevel: number;
  maxStockLevel?: number;
  reorderPoint?: number;
  leadTimeDays?: number;
  unitCost?: number;
  isActive: boolean;
  // Phase 1: ABC Classification & Safety Stock
  abcClassification?: 'A' | 'B' | 'C' | null;
  annualUsageValue?: number;
  safetyStockDays?: number;
  serviceLevelTarget?: number;
  demandVariability?: 'LOW' | 'MEDIUM' | 'HIGH';
  // Phase 1: Compliance & Regulatory
  isRegulated?: boolean;
  regulatoryMinQty?: number;
  regulatoryAuthority?: string;
  regulatoryReference?: string;
  inventoryLevels?: InventoryLevel[];
  aggregated?: {
    totalQuantity: number;
    totalReserved: number;
    totalAvailable: number;
    locationCount: number;
    totalValue?: number;
    daysOfStock?: number | null;
    avgDailyConsumption?: number;
    onOrder?: number;
    lastMovementDate?: string | null;
    expiringBatchesCount?: number;
  };
  status?: StockStatus;
  createdAt: string;
  updatedAt: string;
}

export interface InventoryLevel {
  id: string;
  itemId: string;
  locationId: string;
  location?: { id: string; name: string; code: string; type: string };
  quantity: number;
  reservedQty: number;
  availableQty: number;
  /**
   * Of `quantity`, the part whose title has passed to the customer whose site
   * this is. Zero everywhere that is not a consignment node — so `quantity`
   * stays what is physically there, and `quantity - customerOwnedQty` is what is
   * still ours.
   */
  customerOwnedQty?: number;
  lastCountDate?: string;
  status?: StockStatus;
}

export interface CreateInventoryItemDto {
  sku: string;
  name: string;
  description?: string;
  category: string;
  subcategory?: string;
  unit?: string;
  companyId: string;
  minStockLevel?: number;
  maxStockLevel?: number;
  reorderPoint?: number;
  leadTimeDays?: number;
  unitCost?: number;
  isActive?: boolean;
}

export interface QueryInventoryParams {
  search?: string;
  category?: string;
  locationIds?: string[];
  abcClassifications?: ('A' | 'B' | 'C')[];
  status?: StockStatus;
  companyId?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface RestockDto {
  itemId: string;
  sourceLocationId: string;
  destinationLocationId: string;
  quantity: number;
  unitPrice?: number;
  notes?: string;
}

export interface TransferDto {
  sourceLocationId: string;
  destinationLocationId: string;
  items: { itemId: string; quantity: number }[];
  notes?: string;
}

export interface InventoryDashboard {
  metrics: {
    totalItems: number;
    totalLocations: number;
    totalValue: number;
    activeAlerts: number;
    inTransitShipments: number;
  };
  stockDistribution: {
    optimal: number;
    low: number;
    stockout: number;
    overstock: number;
  };
}

/** Fields a bulk edit may set. Mirrors BulkInventoryChangesDto on the server. */
export interface BulkInventoryChanges {
  category?: string;
  subcategory?: string;
  minStockLevel?: number;
  maxStockLevel?: number;
  reorderPoint?: number;
  leadTimeDays?: number;
  abcClassification?: 'A' | 'B' | 'C';
  safetyStockDays?: number;
  demandVariability?: 'LOW' | 'MEDIUM' | 'HIGH';
  valuationMethod?: 'FIFO' | 'WEIGHTED_AVG';
  isActive?: boolean;
}

export interface BulkUpdateRequest {
  itemIds: string[];
  changes: BulkInventoryChanges;
}

export interface BulkUpdateRowResult {
  id: string;
  sku?: string;
  name?: string;
  status: 'updated' | 'failed';
  reason?: string;
  /** Index signature so this satisfies the shared BulkResultRow contract. */
  [key: string]: unknown;
}

/** A bulk edit applies per item, so the response reports per item. */
export interface BulkUpdateResult {
  requested: number;
  updated: number;
  failed: number;
  fields: string[];
  results: BulkUpdateRowResult[];
}

export const inventoryApi = {
  async getAll(params?: QueryInventoryParams) {
    const response = await api.get<{
      data: InventoryItem[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/inventory', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<InventoryItem>(`/inventory/${id}`);
    return response.data;
  },

  async getDashboard(companyId?: string) {
    const response = await api.get<InventoryDashboard>('/inventory/dashboard', {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },

  async getLevels(id: string) {
    const response = await api.get<InventoryLevel[]>(`/inventory/${id}/levels`);
    return response.data;
  },

  async addLevel(id: string, data: { locationId: string; quantity?: number }) {
    const response = await api.post(`/inventory/${id}/levels`, data);
    return response.data;
  },

  async updateLevel(
    id: string,
    locationId: string,
    data: { quantity: number; reservedQty?: number }
  ) {
    const response = await api.patch(`/inventory/${id}/levels/${locationId}`, data);
    return response.data;
  },

  async restock(data: RestockDto) {
    const response = await api.post('/inventory/restock', data);
    return response.data;
  },

  async transfer(data: TransferDto) {
    const response = await api.post('/inventory/transfer', data);
    return response.data;
  },

  async bulkUpdate(data: BulkUpdateRequest) {
    const response = await api.post<BulkUpdateResult>('/inventory/bulk/update', data);
    return response.data;
  },

  async create(data: CreateInventoryItemDto) {
    const response = await api.post<InventoryItem>('/inventory', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateInventoryItemDto>) {
    const response = await api.patch<InventoryItem>(`/inventory/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<InventoryItem>(`/inventory/${id}`);
    return response.data;
  },

  // ============================================
  // Phase 1: OFS Inventory Enhancement Methods
  // ============================================

  // ABC Classification
  async getABCAnalysis(analysisWindowDays = 365) {
    const response = await api.get<ABCClassification[]>('/inventory/analytics/abc-analysis', {
      params: { analysisWindowDays },
    });
    return response.data;
  },

  async getABCSummary() {
    const response = await api.get<ABCSummary>('/inventory/analytics/abc-summary');
    return response.data;
  },

  async getABCReport() {
    const response = await api.get<ABCReport>('/inventory/analytics/abc-report');
    return response.data;
  },

  async getItemsByABCClassification(classification: 'A' | 'B' | 'C') {
    const response = await api.get<InventoryItem[]>(`/inventory/abc/${classification}`);
    return response.data;
  },

  async applyABCClassifications() {
    const response = await api.post<{ updated: number; errors: string[] }>(
      '/inventory/analytics/apply-abc-classifications'
    );
    return response.data;
  },

  // Safety Stock
  async getSafetyStock(analysisWindowDays = 90, serviceLevelTarget = 0.95) {
    const response = await api.get<SafetyStockCalculation[]>('/inventory/analytics/safety-stock', {
      params: { analysisWindowDays, serviceLevelTarget },
    });
    return response.data;
  },

  async getItemSafetyStock(id: string, serviceLevelTarget = 0.95, analysisWindowDays = 90) {
    const response = await api.get<SafetyStockCalculation>(`/inventory/${id}/safety-stock`, {
      params: { serviceLevelTarget, analysisWindowDays },
    });
    return response.data;
  },

  async getInadequateSafetyStock(threshold = 0.7) {
    const response = await api.get<SafetyStockCalculation[]>(
      '/inventory/analytics/inadequate-safety-stock',
      { params: { threshold } }
    );
    return response.data;
  },

  async applySafetyStockRecommendations(itemIds: string[], serviceLevelTarget = 0.95) {
    const response = await api.post<{ updated: number; errors: string[] }>(
      '/inventory/analytics/apply-safety-stock',
      { itemIds, serviceLevelTarget }
    );
    return response.data;
  },

  // Consumption Pattern Analysis
  async getConsumptionPatterns(analysisWindowDays = 180) {
    const response = await api.get<ConsumptionPattern[]>(
      '/inventory/analytics/consumption-patterns',
      { params: { analysisWindowDays } }
    );
    return response.data;
  },

  async getItemConsumptionPattern(id: string, analysisWindowDays = 180) {
    const response = await api.get<ConsumptionPattern>(`/inventory/${id}/consumption-pattern`, {
      params: { analysisWindowDays },
    });
    return response.data;
  },

  async getJobTypeRates(id: string) {
    const response = await api.get<JobTypeRate[]>(`/inventory/${id}/job-type-rates`);
    return response.data;
  },

  // Critical Spares Ranking
  async getCriticalSpares() {
    const response = await api.get<CriticalSpareRanking[]>('/inventory/analytics/critical-spares');
    return response.data;
  },

  async getCriticalSparesSummary() {
    const response = await api.get<CriticalSparesSummary>(
      '/inventory/analytics/critical-spares-summary'
    );
    return response.data;
  },

  async getSparePartRanking(id: string) {
    const response = await api.get<CriticalSpareRanking>(`/inventory/${id}/spare-part-ranking`);
    return response.data;
  },

  async getSparesByRanking(ranking: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW') {
    const response = await api.get<InventoryItem[]>(`/inventory/spares/${ranking}`);
    return response.data;
  },

  async applySparesRankings() {
    const response = await api.post<{ updated: number; errors: string[] }>(
      '/inventory/analytics/apply-spares-rankings'
    );
    return response.data;
  },

  // ============================================
  // Phase 1: Inventory Enhancement Methods
  // ============================================

  // Stock Adjustment
  async adjustStock(itemId: string, locationId: string, data: AdjustInventoryDto) {
    const response = await api.post(`/inventory/${itemId}/levels/${locationId}/adjust`, data);
    return response.data;
  },

  // Transaction Ledger
  async getTransactions(
    itemId: string,
    params?: {
      locationId?: string;
      startDate?: string;
      endDate?: string;
      type?: string;
      page?: number;
      limit?: number;
    }
  ) {
    const response = await api.get<{
      data: InventoryTransaction[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>(`/inventory/${itemId}/transactions`, { params });
    return response.data;
  },

  async getTransactionSummary(itemId: string, params?: { startDate?: string; endDate?: string }) {
    const response = await api.get<TransactionSummary>(`/inventory/${itemId}/transaction-summary`, {
      params,
    });
    return response.data;
  },

  async getRecentTransactions() {
    const response = await api.get<InventoryTransaction[]>(
      '/inventory/analytics/recent-transactions'
    );
    return response.data;
  },

  // Cycle Counts
  async getCycleCounts(params?: {
    status?: string;
    locationId?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await api.get<{
      data: CycleCount[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>('/inventory/cycle-counts', { params });
    return response.data;
  },

  async getCycleCount(countId: string) {
    const response = await api.get<CycleCount>(`/inventory/cycle-counts/${countId}`);
    return response.data;
  },

  async createCycleCount(data: { locationId: string; countType: string; scheduledDate: string }) {
    const response = await api.post<CycleCount>('/inventory/cycle-counts', data);
    return response.data;
  },

  async startCycleCount(countId: string) {
    const response = await api.post(`/inventory/cycle-counts/${countId}/start`);
    return response.data;
  },

  async recordCycleCounts(
    countId: string,
    items: { itemId: string; countedQty: number; notes?: string }[]
  ) {
    const response = await api.post(`/inventory/cycle-counts/${countId}/record`, { items });
    return response.data;
  },

  async approveCycleCount(countId: string) {
    const response = await api.post(`/inventory/cycle-counts/${countId}/approve`);
    return response.data;
  },

  async cancelCycleCount(countId: string) {
    const response = await api.post(`/inventory/cycle-counts/${countId}/cancel`);
    return response.data;
  },

  // Valuation
  async getValuationReport() {
    const response = await api.get<ValuationReport>('/inventory/valuation/report');
    return response.data;
  },

  async getValuationSummary() {
    const response = await api.get<ValuationSummary>('/inventory/valuation/summary');
    return response.data;
  },

  async recalculateValuations() {
    const response = await api.post('/inventory/valuation/recalculate');
    return response.data;
  },

  async getItemValuation(itemId: string) {
    const response = await api.get(`/inventory/${itemId}/valuation`);
    return response.data;
  },

  // Reservations
  async getReservations(params?: {
    itemId?: string;
    locationId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) {
    const response = await api.get<{
      data: StockReservation[];
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    }>('/inventory/reservations', { params });
    return response.data;
  },

  async createReservation(data: {
    itemId: string;
    locationId: string;
    quantity: number;
    referenceType: string;
    referenceId?: string;
    expiresAt?: string;
    notes?: string;
  }) {
    const response = await api.post<StockReservation>('/inventory/reservations', data);
    return response.data;
  },

  async releaseReservation(reservationId: string) {
    const response = await api.post(`/inventory/reservations/${reservationId}/release`);
    return response.data;
  },

  async fulfillReservation(reservationId: string) {
    const response = await api.post(`/inventory/reservations/${reservationId}/fulfill`);
    return response.data;
  },

  // Recalculate All (scheduler manual trigger)
  async recalculateAll() {
    const response = await api.post('/inventory/analytics/recalculate-all');
    return response.data;
  },

  // ============================================
  // Item Detail Enhancement Methods
  // ============================================

  async getSupplyPipeline(itemId: string) {
    const response = await api.get<SupplyPipeline>(`/inventory/${itemId}/supply-pipeline`);
    return response.data;
  },

  async getItemBatches(itemId: string) {
    const response = await api.get<ItemBatchData>(`/inventory/${itemId}/batches`);
    return response.data;
  },

  async getItemEquipmentLinks(itemId: string) {
    const response = await api.get<ItemEquipmentLinks>(`/inventory/${itemId}/equipment-links`);
    return response.data;
  },
};

// ============================================
// Phase 1: Type Definitions
// ============================================

export interface ABCClassification {
  itemId: string;
  itemSku: string;
  itemName: string;
  classification: 'A' | 'B' | 'C';
  annualUsageValue: number;
  annualQuantity: number;
  unitCost: number;
  percentOfTotalValue: number;
  cumulativePercent: number;
  rank: number;
  controlRecommendations: {
    reviewFrequency: string;
    countingCycle: string;
    safetyStockLevel: string;
    reorderStrategy: string;
  };
  calculatedAt: string;
}

export interface ABCSummary {
  totalItems: number;
  totalValue: number;
  breakdown: Array<{
    classification: 'A' | 'B' | 'C';
    itemCount: number;
    itemPercent: number;
    totalValue: number;
    valuePercent: number;
  }>;
}

export interface ABCReport {
  summary: ABCSummary;
  classifications: ABCClassification[];
  recommendations: string[];
}

export interface SafetyStockCalculation {
  itemId: string;
  itemSku: string;
  itemName: string;
  currentMinStock: number;
  calculatedSafetyStock: number;
  calculatedReorderPoint: number;
  metrics: {
    avgDailyDemand: number;
    stdDevDemand: number;
    leadTimeDays: number;
    serviceLevelTarget: number;
    zScore: number;
  };
  demandVariability: 'LOW' | 'MEDIUM' | 'HIGH';
  recommendation: string;
  calculatedAt: string;
}

export interface ConsumptionPattern {
  itemId: string;
  pattern: 'STEADY_DEMAND' | 'SEASONAL_DEMAND' | 'SPIKE_DEMAND' | 'PROJECT_DRIVEN';
  confidence: number;
  metrics: {
    avgDailyConsumption: number;
    stdDeviation: number;
    coefficientOfVariation: number;
    seasonalityScore?: number;
    projectDependency?: number;
  };
  recommendedForecastMethod: string;
  lastAnalyzedAt: string;
}

export interface JobTypeRate {
  jobType: string;
  avgConsumptionRate: number;
  unit: string;
}

export interface CriticalSpareRanking {
  itemId: string;
  itemSku: string;
  itemName: string;
  ranking: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NOT_ASSESSED';
  /** Null when nobody has judged what a stock-out here would cost. */
  criticalityScore: number | null;
  criticality: Criticality;
  metrics: {
    /** Worst consequence among the assets it serves, or the item's own. */
    equipmentCriticality: number | null;
    /** Only where somebody recorded one — never estimated from the scale. */
    downtimeCostPerHour: number | null;
    mtbf: number | null;
    mttr: number | null;
    leadTimeDays: number;
    supplierReliability: number | null;
  };
  linkedEquipment: Array<{
    equipmentId: string;
    equipmentName: string;
    assetNumber: string;
    criticalityScore: number | null;
  }>;
  stockRecommendation: {
    /** Null when there is no assessment to base a recommendation on. */
    recommendedMinStock: number | null;
    recommendedMaxStock: number | null;
    multiSourceRequired: boolean;
    inspectionFrequency: string;
    basis: string;
  };
  riskFactors: string[];
  calculatedAt: string;
}

export interface CriticalSparesSummary {
  totalSpares: number;
  breakdown: Array<{
    ranking: string;
    count: number;
    percentOfTotal: number;
  }>;
  topCritical: CriticalSpareRanking[];
  stockoutRisks: Array<{
    itemId: string;
    itemSku: string;
    itemName: string;
    ranking: string;
    currentStock: number;
    minStock: number;
    locations: string[];
  }>;
}

// ============================================
// Phase 1: Inventory Enhancements - Types
// ============================================

export interface InventoryTransaction {
  id: string;
  companyId: string;
  itemId: string;
  locationId: string;
  type: string;
  quantity: number;
  balanceBefore: number;
  balanceAfter: number;
  referenceType?: string;
  referenceId?: string;
  batchId?: string;
  unitCost?: number;
  totalCost?: number;
  performedById: string;
  notes?: string;
  createdAt: string;
  item?: { name: string; sku: string };
  location?: { name: string; code: string };
  performedBy?: { fullName: string; email?: string };
}

export interface TransactionSummary {
  itemId: string;
  period: { startDate?: string; endDate?: string };
  totalTransactions: number;
  byType: Record<string, { count: number; totalQty: number; totalCost: number }>;
}

export interface CycleCount {
  id: string;
  companyId: string;
  locationId: string;
  countNumber: string;
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'PENDING_APPROVAL' | 'APPROVED' | 'CANCELLED';
  countType: string;
  scheduledDate: string;
  startedAt?: string;
  completedAt?: string;
  countedBy?: { fullName: string };
  approvedBy?: { fullName: string };
  location?: { name: string; code: string };
  notes?: string;
  items?: CycleCountItem[];
  _count?: { items: number };
  createdAt: string;
}

export interface CycleCountItem {
  id: string;
  cycleCountId: string;
  itemId: string;
  expectedQty: number;
  countedQty?: number;
  varianceQty?: number;
  variancePct?: number;
  adjustmentApproved: boolean;
  notes?: string;
  item?: { name: string; sku: string; unit?: string };
}

export interface StockReservation {
  id: string;
  companyId: string;
  itemId: string;
  locationId: string;
  quantity: number;
  reservedById: string;
  referenceType: string;
  referenceId?: string;
  status: 'ACTIVE' | 'FULFILLED' | 'RELEASED' | 'EXPIRED';
  expiresAt?: string;
  notes?: string;
  item?: { name: string; sku: string };
  location?: { name: string; code: string };
  reservedBy?: { fullName: string };
  createdAt: string;
}

export interface ValuationReport {
  companyId: string;
  asOfDate: string;
  totalItems: number;
  totalInventoryValue: number;
  byCategory: Record<string, { count: number; totalQty: number; totalValue: number }>;
  byABC: Record<string, { count: number; totalValue: number }>;
  items: Array<{
    itemId: string;
    sku: string;
    name: string;
    category: string;
    abcClassification?: string;
    valuationMethod: string;
    unitCost: number;
    totalQuantity: number;
    totalValue: number;
    locationCount: number;
  }>;
}

export interface ValuationSummary {
  totalSKUs: number;
  totalItems: number;
  totalInventoryValue: number;
  avgValuePerSKU: number;
}

export interface AdjustInventoryDto {
  quantity: number;
  reason: 'DAMAGE' | 'THEFT' | 'SHRINKAGE' | 'COUNT_VARIANCE' | 'SAMPLE' | 'CORRECTION' | 'OTHER';
  notes?: string;
  referenceNumber?: string;
}

// ============================================
// Item Detail Enhancement Types
// ============================================

export interface InboundShipment {
  id: string;
  shipmentNumber: string;
  status: string;
  /** Of this item, on this shipment. Not the size of the whole consignment. */
  quantity: number;
  origin: { id: string; name: string; code: string } | null;
  destination: { id: string; name: string; code: string } | null;
  /** The carrier as a record, once somebody has confirmed which one. */
  carrier: { id: string; name: string } | null;
  /** The carrier as typed. The only record of who is moving an unlinked one. */
  carrierText: string | null;
  transportMode: string | null;
  trackingNumber: string | null;
  /** Frequently null. Shown as unknown, never as a computed guess. */
  estimatedArrival: string | null;
  actualDeparture: string | null;
  latestMilestone: { milestone: string; timestamp: string; location: string | null } | null;
  /** Null where the movement is a transfer rather than a purchase. */
  purchaseOrder: { id: string; poNumber: string } | null;
}

export interface BinLocation {
  id: string;
  quantity: number;
  reservedQty: number;
  storageLocationId: string;
  code: string;
  name: string;
  type: string;
  /** Recorded as far down as the yard addresses. Any part may be absent. */
  zone: string | null;
  aisle: string | null;
  rack: string | null;
  level: string | null;
  site: { id: string; name: string; code: string } | null;
  batch: { id: string; batchNumber: string; expiryDate: string | null } | null;
  serialNumbers: string[];
  lastMovedAt: string | null;
}

export interface SupplyPipeline {
  summary: {
    onOrder: number;
    inTransit: number;
    pendingReceipt: number;
    /** How much of what is moving carries no arrival date. */
    inTransitWithoutEta: number;
  };
  inboundShipments: InboundShipment[];
  binLocations: BinLocation[];
  purchaseOrders: Array<{
    id: string;
    poNumber: string;
    poId: string;
    supplier: { id: string; name: string } | null;
    quantity: number;
    receivedQty: number;
    unitPrice: number | null;
    expectedDate: string | null;
    status: string;
  }>;
  recentReceipts: Array<{
    id: string;
    receiptNumber: string;
    receiptId: string;
    receivedAt: string;
    location: { id: string; name: string };
    expectedQty: number;
    receivedQty: number;
    acceptedQty: number;
    rejectedQty: number;
    qualityStatus: string;
    batchNumber: string | null;
  }>;
}

export interface ItemBatch {
  id: string;
  batchNumber: string;
  itemId: string;
  locationId: string;
  location: { id: string; name: string; code: string } | null;
  supplier: { id: string; name: string } | null;
  quantity: number;
  receivedDate: string;
  expiryDate: string | null;
  manufacturingDate: string | null;
  certificateNumber: string | null;
  status: 'AVAILABLE' | 'RESERVED' | 'QUARANTINE' | 'EXPIRED' | 'CONSUMED';
  notes: string | null;
}

export interface ItemBatchData {
  batches: ItemBatch[];
  summary: {
    total: number;
    expired: number;
    expiringSoon: number;
    byStatus: Record<string, number>;
  };
  regulatory: {
    isRegulated: boolean;
    authority: string | null;
    reference: string | null;
    minimumQty: number | null;
  } | null;
}

export interface EquipmentLink {
  id: string;
  equipmentId: string;
  equipmentName: string;
  assetNumber: string;
  category: string;
  equipmentStatus: string;
  equipmentCondition: string;
  criticalityScore: number | null;
  criticalityLevel: string | null;
  recommendedQty: number;
  notes: string | null;
}

export interface ItemEquipmentLinks {
  links: EquipmentLink[];
  summary: {
    totalEquipment: number;
    highestCriticality: string;
    criticalCount: number;
  };
}
